/*
 * File: /apps/web/src/util/reducerHelpers.js
 * Project: tr-web
 * Created Date: 2020-08-04
 * Author: Raif
 *
 * Copyright (c) 2020 TaskRabbit, Inc
 */

// eslint-disable-next-line camelcase
import { snake_caseKeys } from "util/casing";
import deepMerge from "just-extend";
// For api calls we need three action types all named similarly
// which is verbose and tedious.  This function encapsulates that.
const createActionTypesFor = (namespace, entity) => {
  if (!namespace || !entity) {
    throw new Error("Must provide a namespace and entity name");
  }
  const snakeCasedUpperCasedEntity = Object.keys(
    snake_caseKeys({ [entity]: true })
  )[0].toUpperCase();
  return {
    REQUEST: `${namespace}_${snakeCasedUpperCasedEntity}_REQUEST`,
    SUCCESS: `${namespace}_${snakeCasedUpperCasedEntity}_SUCCESS`,
    FAILURE: `${namespace}_${snakeCasedUpperCasedEntity}_FAILURE`,
  };
};

// This function will merge one collection of items with another
// If item exists in both collections the two are merged with
// preference given to newItem
const mergeCollections = ({
  existingItems = [],
  newItems = [],
  itemIdKey = "id",
  optionalTransformation = (x) => x,
}) => {
  const foundItems = [];
  const updatedItems = existingItems.map((eItem) => {
    const found = newItems.find(
      (nItem) => eItem[itemIdKey] === nItem[itemIdKey]
    );
    if (found) {
      const updatedItem = deepMerge(eItem, found);
      foundItems.push(found[itemIdKey]);
      return optionalTransformation(updatedItem);
    }
    return eItem;
  });

  return updatedItems.concat(
    newItems.filter((x) => !foundItems.includes(x[itemIdKey]))
  );
};

// This function will attempt to find and update an item in a collection
// if not present it will just return the original collection
const updateItem = ({
  item,
  itemIdKey,
  collection = [],
  optionalTransformation = (x) => x,
}) => {
  return collection.map((eItem) => {
    if (eItem[itemIdKey] === item[itemIdKey]) {
      const updatedItem = deepMerge(true, eItem, item);
      return optionalTransformation(updatedItem);
    }
    return eItem;
  });
};

// This function will update an item if it exists otherwise insert it
const upsertItem = ({
  item,
  itemIdKey,
  collection = [],
  optionalTransformation = (x) => x,
}) => {
  let targetIndex = -1;

  const updatedItems = collection.map((eItem, idx) => {
    if (eItem[itemIdKey] === item[itemIdKey]) {
      targetIndex = idx;
      const updatedItem = deepMerge(true, eItem, item);
      return optionalTransformation(updatedItem);
    }
    return eItem;
  });

  return targetIndex >= 0
    ? { result: updatedItems, targetIndex }
    : { result: [...collection, item], targetIndex: collection.length };
};

// These functions accommodate the state pattern where you have a
// collection of Taskers which have a collection of categories
// which have a collection of some "TYPE" of item e.g. Tasker->Category->Photos.
// It expects an action which has a taskerId, and categoryId to drill down to
// the target type.
// So the schema would look like this
// [
//   {
//     taskerId: number,
//     anyOtherProperties: any,
//     categories: [
//       {
//         categoryId: number,
//         anyOtherProperties: any,
//         <TYPE>: [
//           {
//             someFormOfId: number,
//             anyOtherProperties: any,
//           },
//         ],
//       },
//     ],
//   },
// ];

const updatePagingOnCategory = ({ action, state }) => {
  if (!state || !action) {
    throw new Error("You must provide action, and state parameters");
  }

  const { result: newTaskers, targetIndex: taskerIndex } = upsertItem({
    item: { taskerId: action.taskerId },
    itemIdKey: "taskerId",
    collection: state.taskers,
  });

  upsertItem({
    // if items are not paged it's ok for action.paging to undefined;
    item: { categoryId: action.categoryId, paging: action.paging },
    itemIdKey: "categoryId",
    collection: newTaskers[taskerIndex].categories,
  });

  return newTaskers;
};

const deepMergeTaskerCategoryItems = ({
  action,
  state,
  collectionPropertyName,
  itemIdKey,
}) => {
  if (!collectionPropertyName || !state || !action) {
    throw new Error(
      "You must provide action, state, and collectionPropertyName parameter"
    );
  }

  const idKey = itemIdKey || `${collectionPropertyName}Id`;
  const { result: newTaskers, targetIndex: taskerIndex } = upsertItem({
    item: { taskerId: action.taskerId },
    itemIdKey: "taskerId",
    collection: state.taskers,
  });

  const { result: newCategories, targetIndex: categoryIndex } = upsertItem({
    // if items are not paged it's ok for action.paging to undefined;
    item: { categoryId: action.categoryId, paging: action.paging },
    itemIdKey: "categoryId",
    collection: newTaskers[taskerIndex].categories,
  });

  const result = mergeCollections({
    existingItems: newCategories[categoryIndex][collectionPropertyName],
    newItems: action[collectionPropertyName],
    itemIdkey: idKey,
  });

  newCategories[categoryIndex][collectionPropertyName] = result;
  newTaskers[taskerIndex].categories = newCategories;
  return newTaskers;
};

const deepUpdateTaskerCategoryItem = ({
  action,
  state,
  collectionPropertyName,
  itemIdKey,
  optionalTransform,
}) => {
  if (!collectionPropertyName || !state || !action) {
    throw new Error(
      "You must provide action, state, and collectionPropertyName parameter"
    );
  }

  const idKey = itemIdKey || `${collectionPropertyName}Id`;

  return state.taskers.map((tasker) => {
    if (tasker.taskerId === action.taskerId) {
      tasker.categories = (tasker.categories || []).map((category) => {
        if (category.categoryId === action.categoryId) {
          category[collectionPropertyName] = updateItem({
            item: action[collectionPropertyName],
            itemIdKey: idKey,
            collection: category[collectionPropertyName],
            optionalTransform,
          });
        }
        return category;
      });
    }
    return tasker;
  });
};

export {
  createActionTypesFor,
  updateItem,
  upsertItem,
  mergeCollections,
  deepMergeTaskerCategoryItems,
  deepUpdateTaskerCategoryItem,
  updatePagingOnCategory,
};
